import React from "react";
import { X } from "lucide-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import BrandLogo from "./BrandLogo";
import ImageCarousel from "./ImageCarousel";
import { useUser } from "../context/UserProvider";
import { useTheme } from "../context/ThemeContext";
import { useRolePermissions } from "../hooks/useRolePermissions";
import {
  getBaseUSD,
  getPriceForCurrency,
  applyLocalDiscount,
  formatPrice,
} from "../utils/priceHelpers";

function d(hit, key, fallback = null) {
  return hit?.data?.[key] ?? hit?.[key] ?? fallback;
}
function formatUpdated(val) {
  if (!val) return "-";
  // Firestore Timestamp has toDate() method
  if (typeof val.toDate === 'function') return val.toDate().toLocaleDateString();
  // Handle seconds-based timestamp (Firestore Timestamp serialized)
  if (val.seconds) return new Date(val.seconds * 1000).toLocaleDateString();
  // If it's already a readable string (contains "/" or "-"), return as-is or format it
  if (typeof val === 'string' && (val.includes('/') || val.includes('-'))) {
    // Parse the date string and format nicely
    const dt = new Date(val.replace(/\//g, '-')); // Convert "2023/09/12" to "2023-09-12"
    if (!isNaN(dt.getTime())) {
      return dt.toLocaleDateString();
    }
    // If parsing fails, return the original string (already human readable)
    return val.split(' ')[0]; // Return just the date part without time
  }
  // Otherwise treat as milliseconds timestamp
  const ms = Number(val);
  if (isNaN(ms)) return "-";
  const dt = new Date(ms);
  return dt.toLocaleDateString();
}

function ReadOnlyField({ label, value, isForest, t }) {
  return (
    <div className="mb-3 select-none">
      <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>{label}</label>
      <input
        readOnly
        tabIndex={-1}
        onFocus={(e) => e.target.blur()}
        className="w-full rounded-md p-2 pointer-events-none caret-transparent"
        style={{
          backgroundColor: isForest ? 'rgba(15,58,48,0.7)' : t.input.background,
          border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.input.border}`,
          color: isForest ? '#e8f5e9' : t.text.primary,
        }}
        value={(value ?? value === 0) ? String(value) : "-"}
      />
    </div>
  );
}

export default function DetailsSheetLite({ open, onClose, hit }) {
  const { settings, pricing, gates, basePrivRole } = useUser();
  const { theme, isForest } = useTheme();
  const { t } = useTranslation();
  const { role } = useRolePermissions();

  const currency = settings?.currency || "USD";

  // Permission to show weight in details page (from custom claims gates.details.weight)
  const canShowWeight = !!(gates?.details?.weight);
  const canSeeUpdated = ["admin", "managerplus", "manager"].includes(String(role || "").toLowerCase());

  const [copied, setCopied] = React.useState(false);

  // Handle back button to close modal
  React.useEffect(() => {
    if (!open) return;

    // Push a new history state when modal opens
    window.history.pushState({ modalOpen: true }, '');

    const handlePopState = (e) => {
      // Close modal when back button is pressed
      onClose();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Clean up: if modal is still open, go back to remove our history entry
      if (open && window.history.state?.modalOpen) {
        window.history.back();
      }
    };
  }, [open, onClose]);
  const copyCode = async () => {
    const toCopy = String(d(hit, "code", hit?.objectID) || "").split(",")[0].trim();
    if (!toCopy) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(toCopy);
      } else {
        const ta = document.createElement("textarea");
        ta.value = toCopy;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        try { document.execCommand("copy"); } catch { }
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.clearTimeout(copyCode._t);
      copyCode._t = window.setTimeout(() => setCopied(false), 3000);
    } catch { }
  };

  // After hooks and functions; guard before using `hit`
  if (!open || !hit) return null;

  const code = d(hit, "code", hit.objectID);
  const brand = d(hit, "brand");
  const img = d(hit, "imgURL");
  const material = d(hit, "material");
  const country = d(hit, "country");
  // For METAL material, prefer weight; otherwise prefer ceramic weight
  const weight = String(material || '').toUpperCase() === 'METAL'
    ? (d(hit, "weight") || d(hit, "weightCeramic"))
    : (d(hit, "weightCeramic") || d(hit, "weight"));
  const updated = d(hit, "dateUpdated") || d(hit, "dateModified") || d(hit, "updatedAt") || d(hit, "lastUpdated");

  const baseUSD = getBaseUSD(hit);
  const baseLocal = getPriceForCurrency(hit, currency);
  const finalLocal = applyLocalDiscount(baseLocal, baseUSD, pricing);
  const displayPrice = formatPrice(finalLocal, currency);



  const cardStyle = {
    backgroundColor: isForest ? '#0f3a30' : theme.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.12)' : theme.surface.cardBorder}`,
    boxShadow: isForest ? '0 4px 12px rgba(0,0,0,0.3)' : `0 4px 12px ${theme.shadow.md}`,
    color: isForest ? '#e8f5e9' : theme.text.primary,
  };

  const inputStyle = {
    backgroundColor: isForest ? 'rgba(15,58,48,0.7)' : theme.input.background,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : theme.input.border}`,
    color: isForest ? '#e8f5e9' : theme.text.primary,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        className={clsx(
          "relative w-full md:w-[760px] max-h-[80vh] overflow-y-auto",
          "rounded-2xl shadow-2xl p-4 md:p-6"
        )}
        style={cardStyle}
      >
        {/* Top: brand/logo + price moved into image wrapper */}
        <div className="bg-white rounded-xl p-2 pt-11 ring-1 ring-slate-200/60 mb-2 relative pb-1">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-2 rounded-md bg-black text-white/90 hover:bg-black/90 z-30"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
          {/* Header row with brand logo (left) and price (right) */}
          <div className="flex items-center justify-between mb-0 pr-10">
            <div className="shrink-0 w-[96px] md:w-[160px] h-[48px] md:h-[72px] flex items-center justify-start overflow-hidden">
              <BrandLogo label={brand} variant="noText" mode="plain" className="h-full max-h-full max-w-full" />
            </div>
            {String(basePrivRole || '').toLowerCase() === 'free' ? (
              <div className="text-sm text-slate-400 text-right">
                {t('converters.upgradeToViewPricing')}
              </div>
            ) : (
              <div className="text-2xl md:text-3xl font-bold text-right" style={{ color: '#4caf50' }}>
                {displayPrice}
              </div>
            )}
          </div>
          <div className="w-full h-[220px] md:h-[280px] z-10 md:z-0 overflow-visible">
            <ImageCarousel
              sources={img}
              alt={code}
              className="w-full h-full"
              imageClassName="object-contain"
              paginationId={`dpag-${String(code || '').replace(/[^a-zA-Z0-9_-]/g, '')}`}
            />
          </div>
          <div className="h-4 flex items-center justify-center ecat-swiper" onClick={(e) => e.stopPropagation()}>
            <div id={`dpag-${String(code || '').replace(/[^a-zA-Z0-9_-]/g, '')}`} className="swiper-pagination" />
          </div>
        </div>


        {/* Details: read-only fields like Settings page */}
        <div className="space-y-1">
          {/* Code field: clickable to copy only the first code segment */}
          <div className="mb-3">
            <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : theme.text.secondary }}>{t('converters.code')}</label>
            <button
              type="button"
              onClick={copyCode}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); copyCode(); } }}
              className="w-full text-left rounded-md p-2"
              style={inputStyle}
              title={t('converters.codeCopied')}
              aria-label={t('converters.code')}
            >
              {String(code || "-")}
            </button>
          </div>
          {brand && <ReadOnlyField label={t('converters.brand')} value={brand} isForest={isForest} t={theme} />}
          {material && <ReadOnlyField label={t('converters.material')} value={material} isForest={isForest} t={theme} />}
          {country && <ReadOnlyField label={t('converters.country')} value={country} isForest={isForest} t={theme} />}
          {canShowWeight && weight && <ReadOnlyField label={t('converters.weightG')} value={weight} isForest={isForest} t={theme} />}
          {canSeeUpdated && <ReadOnlyField label={t('converters.updated')} value={formatUpdated(updated)} isForest={isForest} t={theme} />}
        </div>

        {/* Copy Toast */}
        {copied && (
          <div className="pointer-events-none fixed top-3 left-1/2 -translate-x-1/2 z-[60]">
            <div className="rounded-lg bg-emerald-600 text-white px-3 py-2 shadow-lg ring-1 ring-white/10">
              {t('converters.codeCopied')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
